import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import axios from 'axios';
import '@/App.css';

// Components
import Login from './components/Login';
import Dashboard from './components/Dashboard';
import TechnikerRedirect from './components/TechnikerRedirect';
import Employees from './components/Employees';
import Teams from './components/Teams';
import Inventory from './components/Inventory';
import Fleet from './components/Fleet';
import Telecommunications from './components/Telecommunications';
import Calendar from './components/Calendar';
import BillingOverview from './components/BillingOverview';
import MyBillings from './components/MyBillings';
import Vacation from './components/Vacation';
import Analysis from './components/Analysis';
import Settings from './components/Settings';
import Sidebar from './components/Sidebar';
import ProtectedRoute from './components/ProtectedRoute';
import { Toaster } from './components/ui/sonner';
import AdminVacationOverview from './components/AdminVacationOverview';

// Contexts
import { PermissionsProvider } from './contexts/PermissionsContext';

// API Configuration
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Axios interceptor for authentication
axios.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('authToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Axios interceptor for handling auth errors
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('authToken');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

const App = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const token = localStorage.getItem('authToken');
    if (token) {
      try {
        const response = await axios.get(`${API}/auth/me`);
        setUser(response.data);
        setIsAuthenticated(true);
      } catch (error) {
        console.error('Auth check failed:', error);
        localStorage.removeItem('authToken');
        localStorage.removeItem('user');
        setIsAuthenticated(false);
      }
    } else {
      setIsAuthenticated(false);
    }
    setLoading(false);
  };

  const handleLogin = (userData, token) => {
    localStorage.setItem('authToken', token);
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
    setIsAuthenticated(true);
  };

  const handleLogout = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
    setUser(null);
    setIsAuthenticated(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 mx-auto mb-4"></div>
          <p className="text-slate-600">Lade SKALARTECH System...</p>
        </div>
      </div>
    );
  }

  return (
    <Router>
      <div className="App">
        <Toaster position="top-right" />
        <Routes>
          <Route 
            path="/login" 
            element={
              !isAuthenticated ? (
                <Login onLogin={handleLogin} />
              ) : (
                <Navigate to="/" replace />
              )
            } 
          />
          
          {isAuthenticated ? (
            <Route 
              path="/*" 
              element={
                <PermissionsProvider user={user}>
                  <div className="flex min-h-screen bg-slate-50">
                    <Sidebar user={user} onLogout={handleLogout} />
                    <main className="flex-1 ml-64 p-6">
                      <Routes>
                        <Route path="/" element={<TechnikerRedirect />} />
                        <Route path="/employees" element={
                          <ProtectedRoute module="employees">
                            <Employees />
                          </ProtectedRoute>
                        } />
                        <Route path="/teams" element={
                          <ProtectedRoute module="teams">
                            <Teams />
                          </ProtectedRoute>
                        } />
                        <Route path="/inventory" element={
                          <ProtectedRoute module="inventory">
                            <Inventory />
                          </ProtectedRoute>
                        } />
                        <Route path="/fleet" element={
                          <ProtectedRoute module="fleet">
                            <Fleet />
                          </ProtectedRoute>
                        } />
                        <Route path="/telecommunications" element={
                          <ProtectedRoute module="telecommunications">
                            <Telecommunications />
                          </ProtectedRoute>
                        } />
                        <Route path="/calendar" element={
                          <ProtectedRoute module="calendar">
                            <Calendar />
                          </ProtectedRoute>
                        } />
                        <Route path="/vacation" element={
                          <ProtectedRoute module="vacation">
                            <Vacation />
                          </ProtectedRoute>
                        } />
                        <Route path="/analysis" element={
                          <ProtectedRoute module="analytics">
                            <Analysis />
                          </ProtectedRoute>
                        } />
                        <Route path="/billing-overview" element={
                          <ProtectedRoute module="billing">
                            <BillingOverview />
                          </ProtectedRoute>
                        } />
                        <Route path="/my-billings" element={
                          <ProtectedRoute module="billing">
                            <MyBillings />
                          </ProtectedRoute>
                        } />
                        <Route path="/settings" element={
                          <ProtectedRoute module="settings">
                            <Settings />
                          </ProtectedRoute>
                        } />
                      </Routes>
                    </main>
                  </div>
                </PermissionsProvider>
              } 
            />
          ) : (
            <Route path="*" element={<Navigate to="/login" replace />} />
          )}
        </Routes>
      </div>
    </Router>
  );
};

export default App;